"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function WordCounter() {
  const [text, setText] = useState("");
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "word-counter") ?? tools[0],
    locale
  );

  const stats = {
    words: text.trim() === "" ? 0 : text.trim().split(/\s+/).length,
    characters: text.length,
    charactersNoSpaces: text.replace(/\s/g, "").length,
    sentences: text.trim() === "" ? 0 : text.split(/[.!?]+/).filter(Boolean).length,
    paragraphs: text.trim() === "" ? 0 : text.split(/\n+/).filter(Boolean).length,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification
  };

  const handleClear = () => {
    setText("");
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
             <Icon icon="mdi:counter" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-4 text-center shadow-[0_5px_0_0_#191A23] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_0_0_#191A23]">
          <div className="text-4xl font-bold text-positivus-dark">{stats.words}</div>
          <div className="text-sm text-gray-500 mt-1 font-medium">{ui.words}</div>
        </div>
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-4 text-center shadow-[0_5px_0_0_#191A23] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_0_0_#191A23]">
          <div className="text-4xl font-bold text-positivus-dark">{stats.characters}</div>
          <div className="text-sm text-gray-500 mt-1 font-medium">{ui.characters}</div>
        </div>
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-4 text-center shadow-[0_5px_0_0_#191A23] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_0_0_#191A23]">
          <div className="text-4xl font-bold text-positivus-dark">{stats.sentences}</div>
          <div className="text-sm text-gray-500 mt-1 font-medium">{ui.sentences}</div>
        </div>
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-4 text-center shadow-[0_5px_0_0_#191A23] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_0_0_#191A23]">
          <div className="text-4xl font-bold text-positivus-dark">{stats.paragraphs}</div>
          <div className="text-sm text-gray-500 mt-1 font-medium">{ui.paragraphs}</div>
        </div>
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-4 text-center shadow-[0_5px_0_0_#191A23] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_0_0_#191A23]">
          <div className="text-4xl font-bold text-positivus-dark">{stats.charactersNoSpaces}</div>
          <div className="text-sm text-gray-500 mt-1 font-medium">{ui.noSpaces}</div>
        </div>
      </div>

      <div className="relative">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={ui.typeOrPasteTextHere}
          className="min-h-[400px] w-full resize-y rounded-[30px] border-2 border-positivus-dark bg-white p-6 text-lg text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all shadow-sm"
        />
        <div className="absolute bottom-6 right-6 flex gap-3">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 rounded-[15px] bg-positivus-green px-5 py-2.5 text-sm font-bold text-positivus-dark hover:bg-positivus-green/80 transition-all border-2 border-positivus-dark shadow-[0_4px_0_0_#191A23] hover:translate-y-[2px] hover:shadow-none"
          >
            <Icon icon={uiIcons.copy} className="h-4 w-4" />
            {ui.copy}
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-2 rounded-[15px] bg-white px-5 py-2.5 text-sm font-bold text-positivus-dark hover:bg-gray-50 transition-all border-2 border-positivus-dark shadow-[0_4px_0_0_#191A23] hover:translate-y-[2px] hover:shadow-none"
          >
            <Icon icon={uiIcons.refresh} className="h-4 w-4" />
            {ui.clear}
          </button>
        </div>
      </div>
    </div>
  );
}
